import React from 'react';
import { Badge, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import { useTranslation } from '../../../common/hooks/useTranslation';
import {
  KSFStatusEnum,
  SalesInvoiceStatusEnum,
  SalesInvoiceStatusObj,
} from '../../../common/pokCore/validation/enums';
import { GetExtendedSalesInvoiceDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';

const STATUS_VARIANTS: Record<SalesInvoiceStatusEnum, string> = {
  [SalesInvoiceStatusEnum.Draft]: 'secondary',
  [SalesInvoiceStatusEnum.Generated]: 'primary',
  [SalesInvoiceStatusEnum.Sent]: 'info',
  [SalesInvoiceStatusEnum.InKSeF]: 'success',
  [SalesInvoiceStatusEnum.Paid]: 'success',
};

const getStatusVariant = (salesInvoice?: GetExtendedSalesInvoiceDto) =>
  salesInvoice?.statusKsef === KSFStatusEnum.RejectedInKSeF
    ? 'danger'
    : STATUS_VARIANTS[
        (salesInvoice?.status as SalesInvoiceStatusEnum) ||
          SalesInvoiceStatusEnum.Draft
      ];

const canPrintInvoice = (salesInvoice?: GetExtendedSalesInvoiceDto) =>
  (!salesInvoice?.toKsef &&
    salesInvoice?.status !== SalesInvoiceStatusEnum.Draft) ||
  (salesInvoice?.toKsef &&
    salesInvoice?.status === SalesInvoiceStatusEnum.InKSeF) ||
  (salesInvoice?.toKsef &&
    salesInvoice.statusKsef === KSFStatusEnum.OFFLINE &&
    salesInvoice?.status === SalesInvoiceStatusEnum.Sent);

interface PrintIconProps {
  salesInvoice?: GetExtendedSalesInvoiceDto;
  onPrint?: () => void;
  onPreview?: () => void;
}

const PrintIcon: React.FC<PrintIconProps> = ({
  salesInvoice,
  onPrint,
  onPreview,
}) => {
  const { t, tk } = useTranslation('fvsLng');

  if (!salesInvoice?.id || !salesInvoice.active) {
    return null;
  }

  if (canPrintInvoice(salesInvoice) && onPrint) {
    return (
      <OverlayTrigger
        overlay={<Tooltip>{t(tk.finance.openInvoicePrint)}</Tooltip>}
      >
        <Icon.FilePdf
          className="icon-blue cursor-pointer"
          size={20}
          onClick={onPrint}
        />
      </OverlayTrigger>
    );
  }

  if (onPreview) {
    return (
      <OverlayTrigger
        overlay={<Tooltip>{t(tk.finance.previewInvoice)}</Tooltip>}
      >
        <Icon.FilePdf
          className="icon-red cursor-pointer"
          size={20}
          onClick={onPreview}
        />
      </OverlayTrigger>
    );
  }

  return null;
};

interface SalesInvoiceHeaderTitleProps {
  isInActive?: boolean;
  salesInvoice?: GetExtendedSalesInvoiceDto;
  goToSalesInvoice: (id: string) => void;
  onPrintInvoice?: () => void;
  onPreviewInvoice?: () => void;
}

export const SalesInvoiceHeaderTitle: React.FC<
  SalesInvoiceHeaderTitleProps
> = ({
  isInActive,
  salesInvoice,
  goToSalesInvoice,
  onPrintInvoice,
  onPreviewInvoice,
}) => {
  const { t, tk } = useTranslation('fvsLng');

  const status = salesInvoice?.status || SalesInvoiceStatusEnum.Draft;
  const originalInvoice = salesInvoice?.parent
    ? salesInvoice.originalInvoice
    : undefined;
  const sentToSymphonyHandelTs =
    salesInvoice?.status === SalesInvoiceStatusEnum.Sent
      ? salesInvoice.sentToSymphonyHandelTs
      : undefined;

  return (
    <div className="d-flex flex-wrap align-items-center column-gap-2 row-gap-1">
      {isInActive && <Badge bg="dark">{t(tk.finance.archiveUpperCase)}</Badge>}
      <span className={isInActive ? 'text-decoration-line-through' : undefined}>
        {salesInvoice?.parent
          ? t(tk.finance.correction)
          : t(tk.finance.saleInvoice)}{' '}
        {salesInvoice?.number && <b>{salesInvoice.number}</b>}
      </span>
      <PrintIcon
        salesInvoice={salesInvoice}
        onPrint={onPrintInvoice}
        onPreview={onPreviewInvoice}
      />
      {originalInvoice?.id && (
        <span className="fs-6 fw-normal text-muted">
          {t(tk.finance.toSaleInvoice)}{' '}
          <Button
            variant="link"
            className="p-0 align-baseline"
            onClick={() => goToSalesInvoice(originalInvoice.id!)}
          >
            {originalInvoice.number}
          </Button>
        </span>
      )}
      <Badge bg={getStatusVariant(salesInvoice)} className="fs-6 fw-semibold">
        {t(SalesInvoiceStatusObj[status]).toUpperCase()}
      </Badge>
      {sentToSymphonyHandelTs && (
        <span className="fs-6 fw-normal text-muted">
          {t(tk.finance.toSHWithDate)} {momencik(sentToSymphonyHandelTs)}
        </span>
      )}
      {!!salesInvoice?.systemNumber && (
        <span className="fs-6 fw-normal text-muted">
          {t(tk.common.sysNumberLowerCase)} {salesInvoice.systemNumber}
        </span>
      )}
    </div>
  );
};
